import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG } from '../authentication/constants';
import { MsalService } from '../authentication/msal.service';
import { MsalTokenHelper } from '../authentication/msal-token-helper';
import { MsalGuardConfiguration } from '../authentication/msal.guard.config';
import { MsalInteractionRecoveryService } from '../authentication/msal-interaction-recovery.service';
import { Location } from '@angular/common';
import { Observable, of } from 'rxjs';
import { AccountInfo, BrowserAuthError, BrowserAuthErrorMessage } from '@azure/msal-browser';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ROUTING_PATHS } from '../../app-routing.module';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private tokenHelper: MsalTokenHelper,
    private http: HttpClient,
    private location: Location,
    private router: Router,
    private interactionRecovery: MsalInteractionRecoveryService,
  ) {}

  /**
   * Builds the absolute url for the destination page
   * @param path Relative path of requested page
   * @returns Full destination url
   */
  getDestinationUrl(path: string): string {
    // Absolute base url for the application (default to origin if base element not present)
    const baseElements = document.getElementsByTagName('base');
    const baseUrl = this.location.normalize(baseElements.length ? baseElements[0].href : window.location.origin);

    // Path of page (including hash, if using hash routing)
    const pathUrl = this.location.prepareExternalUrl(path);

    // Hash location strategy
    if (pathUrl.startsWith('#')) {
      return `${baseUrl}/${pathUrl}`;
    }

    // If using path location strategy, pathUrl will include the relative portion of the base path (e.g. /base/page).
    // Since baseUrl also includes /base, can just concatentate baseUrl + path
    return `${baseUrl}${path}`;
  }

  logout(postLogoutRedirectUri?: string): void {
    this.revokeServerSession()
      .pipe(
        finalize(() => {
          this.tokenHelper.clear();
          this.attemptMsalLogout(postLogoutRedirectUri, false);
        }),
      )
      .subscribe();
  }

  getAccounts(): AccountInfo[] {
    return this.authService.getAllAccounts();
  }

  private attemptMsalLogout(postLogoutRedirectUri: string | undefined, retried: boolean): void {
    this.authService
      .logout({ postLogoutRedirectUri })
      .pipe(
        catchError((err: unknown) => {
          if (this.isInteractionInProgress(err)) {
            if (!retried) {
              console.warn('[LoginService] MSAL interaction_in_progress on logout; clearing stale state and retrying once.');
              this.interactionRecovery.clearStaleInteractionState();
              this.attemptMsalLogout(postLogoutRedirectUri, true);
            } else {
              console.warn('[LoginService] MSAL interaction_in_progress persisted after retry; navigating to /logout locally.');
              this.navigateToLocalLogout();
            }
          } else {
            console.error('[LoginService] MSAL logout failed', err);
          }
          return of(null);
        }),
      )
      .subscribe();
  }

  private isInteractionInProgress(err: unknown): boolean {
    return (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorMessage.interactionInProgress.code
    );
  }

  private navigateToLocalLogout(): void {
    this.router.navigate([ROUTING_PATHS.logout]);
  }

  private revokeServerSession(): Observable<unknown> {
    return this.http.post(`${environment.baseUrl}/auth/logout`, null).pipe(
      catchError(() => of(null)),
    );
  }
}
